import { useState, useEffect } from "react";
import {
    Modal,
    Button,
    Checkbox,
    Stack,
    Group,
    Text,
    Alert,
    Divider,
    TextInput,
    Pagination,
} from "@mantine/core";
import { FaSearch } from "react-icons/fa";
import ModalButtons from "components/ui/Modal/ModalButtons";

interface Account {
    name: string;
    accountId: string;
}

interface ContractManufacturerModalProps {
    opened: boolean;
    onClose: () => void;
    accounts: Account[];
    onAssign: (accountId: string, accountName: string) => void;
    isLoading?: boolean;
}

const ContractManufacturerModal: React.FC<ContractManufacturerModalProps> = ({
    opened,
    onClose,
    accounts,
    onAssign,
    isLoading = false,
}) => {
    const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);

    // Filter accounts based on search query
    const filteredAccounts = accounts.filter(account =>
        account.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination
    const ITEMS_PER_PAGE = 5;
    const totalPages = Math.ceil(filteredAccounts.length / ITEMS_PER_PAGE);
    const paginatedAccounts = filteredAccounts.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        // Reset selections and search when modal opens
        if (opened) {
            setSelectedAccount(null);
            setSearchQuery("");
            setCurrentPage(1);
        }
    }, [opened]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const handleAccountToggle = (accountId: string) => {
        // Only allow selecting one account
        setSelectedAccount(prev => prev === accountId ? null : accountId);
    };

    const handleAssign = () => {
        if (selectedAccount) {
            const account = accounts.find(acc => acc.accountId === selectedAccount);
            onAssign(selectedAccount, account?.name || '');
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Select 1 available partners from the list below"
            size="md"
            centered
        >
            <Stack gap="md">
                <TextInput
                    placeholder="Search Dante Development Partners..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.currentTarget.value)}
                    leftSection={<FaSearch />}
                    size="sm"
                />

                <Divider />

                <Stack gap="xs" mah={300} style={{ overflowY: 'auto' }}>
                    {paginatedAccounts.map((account) => (
                        <Checkbox
                            key={account.accountId}
                            label={account.name}
                            checked={selectedAccount === account.accountId}
                            onChange={() => handleAccountToggle(account.accountId)}
                            color="gray"
                            size="sm"
                        />
                    ))}
                </Stack>

                {totalPages > 1 && (
                    <Group justify="center">
                        <Pagination
                            size="sm"
                            total={totalPages}
                            value={currentPage}
                            onChange={setCurrentPage}
                            withEdges={false}
                        />
                    </Group>
                )}

                {filteredAccounts.length === 0 && searchQuery && (
                    <Alert color="yellow">
                        <Text size="sm">No Dante Development Partners found matching &ldquo;{searchQuery}&rdquo;.</Text>
                    </Alert>
                )}

                {accounts.length === 0 && (
                    <Alert color="yellow">
                        <Text size="sm">No available Dante Development Partners to assign.</Text>
                    </Alert>
                )}

                <ModalButtons>
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAssign}
                        disabled={!selectedAccount}
                        loading={isLoading}
                    >
                        Assign
                    </Button>
                </ModalButtons>
            </Stack>
        </Modal>
    );
};

export default ContractManufacturerModal;
